/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, IconButton, Link} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef, useState, useEffect} from 'react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {ArrowLeftIcon, ArrowRightIcon, PackageIcon, TagIcon} from '@primer/octicons-react'

import styles from './ImmutableActionsMigrationDialog.module.css'
import {clsx} from 'clsx'

interface ImmutableActionsMigrationDialogProps {
  repoName: string
  releaseTags: string[]
  nonSemverTags: string[]
  migratePath: string
  setMigrationDialogOpen: (open: boolean) => void
  setHasUnmigratableTagsDialogOpen: (open: boolean) => void
  setMigrationStatus: (status: string | null) => void
}

export function ImmutableActionsMigrationDialog({
  repoName,
  releaseTags,
  nonSemverTags,
  migratePath,
  setMigrationDialogOpen,
  setHasUnmigratableTagsDialogOpen,
  setMigrationStatus,
}: ImmutableActionsMigrationDialogProps) {
  const returnFocusRef = useRef(null)
  const numOfSemVerTags = releaseTags.length - nonSemverTags.length
  const [error, setError] = useState<string | null>(null)
  const [errorCount, setErrorCount] = useState<number>(0)

  useEffect(() => {
    if (errorCount > 3) {
      window.location.reload()
    }
  }, [errorCount])

  async function handleSubmit() {
    const formData = new FormData()

    const result = await verifiedFetch(migratePath, {
      method: 'POST',
      body: formData,
    })

    if (result.ok) {
      setMigrationStatus('in_progress')
      setMigrationDialogOpen(false)
    } else {
      setError(
        'Failed to start migration, please try again. If this error persists, please ensure you are authenticated with the organization that owns this repository.',
      )
      setErrorCount(prevCount => prevCount + 1)
    }
  }

  return (
    <Dialog
      returnFocusRef={returnFocusRef}
      onClose={() => {
        setMigrationDialogOpen(false)
        setError(null)
      }}
      width="large"
      aria-labelledby="header"
      title={
        <div className="d-flex">
          {nonSemverTags.length > 0 && (
            <IconButton
              icon={ArrowLeftIcon}
              size="small"
              className={clsx('mr-2', styles.IconButton)}
              aria-labelledby="back"
              variant="invisible"
              data-testid="ia-migration-back"
              onClick={() => {
                setMigrationDialogOpen(false)
                setHasUnmigratableTagsDialogOpen(true)
                setError(null)
              }}
            />
          )}
          <span data-testid="ia-migration-title" className={styles.Text}>
            Create immutable action
          </span>
        </div>
      }
      renderFooter={() => {
        return (
          <Dialog.Footer>
            <Link href="https://docs.github.com/actions" className={styles.Link}>
              Learn more
            </Link>
            <Button type="button" onClick={() => setMigrationDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" onClick={handleSubmit} data-testid="ia-migration-button-dialog">
              Create immutable action
            </Button>
          </Dialog.Footer>
        )
      }}
    >
      <div data-testid="inner">
        {error && (
          <Flash variant="danger" className="tmp-mb-3">
            {error}
          </Flash>
        )}
        <div className="d-flex my-2" style={{margin: 'auto', justifyContent: 'center'}}>
          <div className={styles.Box}>
            <TagIcon size={16} className={clsx('mr-1', styles.Octicon)} />
            {pluralize('tag', numOfSemVerTags)}
          </div>
          <ArrowRightIcon size={16} className={clsx('mx-2', styles.Text)} />
          <div className={styles.Box}>
            <PackageIcon size={16} className={clsx('mr-1', styles.Octicon)} />
            {pluralize('version', numOfSemVerTags)}
          </div>
        </div>
        <div>
          <p>
            <strong>
              {numOfSemVerTags} out of {releaseTags.length} tags
            </strong>{' '}
            from <strong>{repoName}</strong> will be recreated as immutable package versions.
          </p>
          <p>
            After this process you can activate your package, at which point references to semantic versions of your
            action will be resolved from immutable package versions rather than git references. Non-semantic version
            references to your action will continue to be resolved from git references.
          </p>
        </div>
      </div>
    </Dialog>
  )
}

function pluralize(word: string, count: number) {
  return `${count} ${word}${count > 1 ? 's' : ''}`
}
